<template>
    <section class="mb-8" v-if="history.length > 0">
        <h4 class="text-slate-800 dark:text-slate-100 font-medium mb-4">Changes history</h4>
        <ul>
            <li
                v-for="(entry, index) in visibleHistory"
                :key="entry.id"
                class="flex items-start gap-3 relative"
                :class="index < visibleHistory.length - 1 ? 'pb-3' : ''"
            >
                <span
                    class="w-1 h-1 rounded-full bg-[#64748B] mt-[7px] shrink-0"
                    style="filter: drop-shadow(0 0 0 #FFF);"
                ></span>
                <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
                        {{ formatEntry(entry) }}
                    </p>
                    <p class="text-xs text-slate-400">
                        {{ formatDate(entry.created_at) }}
                    </p>

                    <div
                        v-if="entry.change_type === 'updated' && entry.changed_fields?.length"
                        class="mt-2"
                    >
                        <div
                            class="relative rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden cursor-pointer"
                            :class="{ 'h-[80px]': !expandedEntries[entry.id] && entry.changed_fields.length > 1 }"
                            @click="entry.changed_fields.length > 1 ? toggleExpanded(entry.id) : null"
                        >
                            <div class="flex flex-col items-start gap-3 px-4 py-3">
                                <div
                                    v-for="field in entry.changed_fields"
                                    :key="field"
                                >
                                    <p class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400">{{ formatFieldLabel(field) }}</p>
                                    <p class="text-sm font-normal leading-5 text-[#1E293B] dark:text-slate-100">
                                        <span class="line-through text-[#1E293B] dark:text-slate-100">{{ formatFieldValue(field, entry.previous_data?.[field]) }}</span>
                                        <span class="mx-1">&rarr;</span>
                                        <span>{{ formatFieldValue(field, entry.new_data?.[field]) }}</span>
                                    </p>
                                </div>
                            </div>
                            <!-- Fade gradient when collapsed -->
                            <div
                                v-if="!expandedEntries[entry.id] && entry.changed_fields.length > 1"
                                class="absolute bottom-0 left-0 right-0 pointer-events-none"
                                :style="fadeGradientStyle"
                            ></div>
                            <!-- Chevron inside box bottom-right -->
                            <button
                                v-if="entry.changed_fields.length > 1"
                                @click.stop="toggleExpanded(entry.id)"
                                class="absolute bottom-2 right-3 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                            >
                                <svg
                                    width="20" height="20" viewBox="0 0 20 20" fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="transition-transform"
                                    :class="{ 'rotate-180': expandedEntries[entry.id] }"
                                >
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </li>
        </ul>
        <button
            v-if="history.length > maxVisible"
            @click="showAll = !showAll"
            class="text-sm font-medium text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:text-slate-300 mt-3"
        >
            {{ showAll ? 'Show less' : 'Show all' }}
        </button>
    </section>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import dayjs from 'dayjs';
import { Apii } from '@/services/api';
import { useThemeStore } from '@/store/theme';

const ApiService = new Apii();
const themeStore = useThemeStore();

const fadeGradientStyle = computed(() => {
    const endColor = themeStore.isDark ? '#1e293b' : '#FFF';
    return `height: 32px; background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, ${endColor} 100%);`;
});

const props = defineProps({
    redemption: {
        type: Object,
        default: null,
    },
});

const history = ref([]);
const expandedEntries = reactive({});
const showAll = ref(false);
const maxVisible = 4;

const visibleHistory = computed(() => {
    if (showAll.value || history.value.length <= maxVisible) return history.value;
    return history.value.slice(0, maxVisible);
});

const toggleExpanded = (entryId) => {
    expandedEntries[entryId] = !expandedEntries[entryId];
};

const fetchHistory = async () => {
    if (!props.redemption?.id) return;

    try {
        const response = await ApiService.apiCall(
            `/redemptions/${props.redemption.id}/history`,
            'get'
        );
        history.value = response.data || [];
    } catch (err) {
        console.error('Failed to fetch redemption history:', err);
        history.value = [];
    }
};

const fieldLabels = {
    name: 'Name',
    start_date: 'Check-in date',
    end_date: 'Check-out date',
    confirmation_number: 'Confirmation number',
    payment_cash_amount: 'Price. Cash cost',
    payment_points: 'Price. Points',
    payment_taxes_fees: 'Price. Total taxes & fees',
    payment_type: 'Payment type',
    class_of_service: 'Class of service',
    number_of_travelers: 'Number of travelers',
    loyalty_number: 'Loyalty number',
    other_location: 'Location',
    traveler_names: 'Traveler names',
};

const formatFieldLabel = (field) => {
    return fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatFieldValue = (field, value) => {
    if (value === null || value === undefined || value === '') return '—';

    if (field === 'payment_cash_amount' || field === 'payment_taxes_fees') {
        const num = parseFloat(value);
        return isNaN(num) ? value : `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    if (field === 'payment_points') {
        const num = parseInt(value, 10);
        return isNaN(num) ? value : num.toLocaleString('en-US');
    }

    if (field === 'start_date' || field === 'end_date') {
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format('MMM D, YYYY') : value;
    }

    if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '—';
    }

    return String(value);
};

const formatEntry = (entry) => {
    const userName = entry.user?.name || 'System';

    switch (entry.change_type) {
        case 'created':
            if (entry.source === 'email_import') {
                return 'Created from email import';
            }
            return `Created by ${userName}`;

        case 'trip_changed': {
            const previousTripId = entry.previous_data?.trip_id;
            const previousTripTitle = entry.previous_data?.trip_title;
            const newTripTitle = entry.new_data?.trip_title;

            if (!previousTripId) {
                return `Added to trip from Unfiled by ${userName}`;
            }

            return previousTripTitle
                ? `Moved from trip '${previousTripTitle}' by ${userName}`
                : `Moved to a different trip by ${userName}`;
        }

        case 'updated':
            if (entry.source === 'email_import') {
                return 'Updated from email import';
            }
            return `Updated by ${userName}`;

        default:
            return `Changed by ${userName}`;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).format('MMM D, YYYY');
};

watch(
    () => props.redemption?.id,
    (newId) => {
        if (newId) {
            fetchHistory();
        }
    },
    { immediate: true }
);
</script>
